import { Injectable, QueryList, ViewChildren, Directive } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { LoaderChannel } from '../../classes/loaderChannel.class';
import { ChannelName, IChannelList, INewChannel } from '../../models/interfaces';

// We have to have @params here for intellisense and on the constructor for Compodoc.
/**
 * **Warning: this service is under heavy development and it's API may change. This documentation is a work in progress.**
 *
 * The loader service contains an observable bus that is in charge of receiving events via the respective channel's set methods,
 * computing the new loading state, and dispatching the current state of the loading channels to it's observable `channels` property.
 */
@Directive()
@Injectable()
export class LoaderBusService {
    @ViewChildren('DisableOnGlobalLoad') disableOnGlobalForms: QueryList<UntypedFormGroup>;
    @ViewChildren('DisableOnHttpLoad') disableOnHttpForms: QueryList<UntypedFormGroup>;

    /** @ignore Should we be subscribed to the individual channel observables. Used in the channel subscription's `takeWhile` */
    _active = true;

    /** Built in channels with limited access. */
    _globalChannels: IChannelList = {
        /** A composite loading channel that returns true if the loader service isn't disabled and any non-composite channel is true and. _Respects the `force` loading channel_. */
        global: new LoaderChannel(false),
        /** Returns true if an HTTP request is in flight. */
        http: new LoaderChannel(false),
        // force: new LoaderChannel(false, () => this._globalChannels.global.setStatus = true),
        /** Can be manually set to force top level loaders in to a true or false state. */
        force: new LoaderChannel(false),
        /** Force the loader's global channel in to an off state. */
        disable: new LoaderChannel(false),
        /** Returns true if any user created or inline loader channels are true. */
        subchannels: new LoaderChannel(false),
        /**
		 * A composite loading channel that returns true if any HTTP request is in flight **and** no subchannels are in flight.
		 * Ideal for a top level loader, because you would only want to show that loader when there's activity and no inline loaders are on the screen.
		 * _Respects the `force` loading channel_.
		 */
        app: new LoaderChannel(false)
    };

    /** Loading channels that are used by inline loaders or are manually managed. */
    _localChannels: IChannelList = {};

    /** The subscriptions that keep our composite loading channel states up to date. */
    _channelSubscriptions = {
        /** Update the global state as needed. */
        compositeGlobal$: combineLatest(
            this._globalChannels.disable.getStatus,
            this._globalChannels.http.getStatus,
            this._globalChannels.force.getStatus,
            this._globalChannels.subchannels.getStatus
        )
            .pipe(takeWhile(() => this._active))
            .subscribe(([ disabled, ...results ]) => (this._globalChannels.global.setStatus = disabled ? false : results.some((res) => res)))

        // global: this._globalChannels.global.getStatus.subscribe((loadingStatus) => {
        //   this.disableOnGlobalForms.map((form) => {
        //     loadingStatus ? form.disable() : form.enable();
        //   });
        // })

        //   subchannels$: combineLatest(...this.getChannelList$())
        //     .pipe(takeWhile(() => this._active))
        //     .subscribe((results) => {
        //       this._globalChannels.subchannels.loading = (results.some((res) => res));
        //     }),

        //   app$: combineLatest(this._globalChannels.http.getStatus, this._globalChannels.force.getStatus)
        //   .pipe(takeWhile(() => this._active))
        //   .subscribe((results) => {
        //       this._globalChannels.app.loading = results.some((res) => res);
        //   })
    };

    /** Trying to refactor global state composing out of the observable above. */
    computeGlobal = (): void => {
        combineLatest(this._globalChannels.http.getStatus, this._globalChannels.force.getStatus, this._globalChannels.subchannels.getStatus)
            .pipe(takeWhile(() => this._active))
            .subscribe((results) => {
                this._globalChannels.global.setStatus = results.some((res) => res);
            });
    };

    /** Convert a table of channels in to an array of observables. Uses the local channel listing by default. */
    getChannelList$ (list: IChannelList = this._localChannels): Array<Observable<boolean>> {
        console.log('getting updated local channel listing');
        return Object.keys(list).map((key) => list[key].getStatus);
    }

    /**
	 * Create and start tracking new local loader channel(s).
	 * @param create The details about the new channel(s) to added to the event bus.
	 */
    createChannels = (create: INewChannel | Array<INewChannel>) => {
        const requestedChannels: Array<INewChannel> = Array.isArray(create) ? create : [ create ];

        requestedChannels.map((requestedChannel) => {
            const name = requestedChannel.name || 'unnamed';
            const status = requestedChannel.initialStatus || false;

            if (
                !name ||
				name === 'global' ||
				name === 'disable' ||
				name === 'http' ||
				name === 'force' ||
				name === 'subchannels' ||
				name === 'app'
            ) {
                console.error(`The channel name (${name}) is invalid and the loading channel cannot be created.`);
            }

            if (!this._localChannels[name]) {
                this._localChannels[name] = new LoaderChannel(status);
            }
        });
    };

    /** Remove a local channel */
    removeChannel = (name: string) => {
        if (this._localChannels[name]) {
            delete this._localChannels[name];
            // delete this._channelStates[name];
            // this._subchannels.splice((this._subchannels.indexOf(name) - 1), 1);
        }
        return this._localChannels;
    };

    /**
	 * An observable containing the state of all tracked channels.
	 *
	 *  @example
	 *  loading = _loader.getStatus;
	 *  ...
	 *  <div *ngIf="(loading | async)" >
	 *    <p *ngIf="loading.http">HTTP Request in Progress</p>
	 *  </div>
	 * */
    public get channels () {
        // return this._channelStates.asObservable();
        return this._globalChannels;
        // return { ...this._globalChannels, ...this._localChannels};
    }

    /**
	 * Manually set a status on the `force` channel, which is taken in to account by the global status.
	 * Note that if you want to force the hub in to a non-loading state, use the disable function.
	 *
	 * @param status The new status. True means the hub will be forced into a loading state. `false` means the hub will behave normally.
	 */
    force = (status: boolean) => {
        this._globalChannels.force.setStatus = status;
    };

    /**
	 * Manually set a status on the `disable` channel, which is taken in to account by the global status. Note that this only disabled composite channels like `global`.
	 * Lower level channels like `HTTP` will continue to report any activity changes on their channel to the respective observable.
	 *
	 * @param status The new status. True means the hub will be forced into a disabled state. `false` means the hub will behave normally.
	 */
    disable (status: boolean) {
        return (this._globalChannels.disable.setStatus = status);
    }

    /**
	 * Manually force the application in to a loading state while a provided router is in navigation states. The loaders subscription to navigation events returned.
	 *
	 * @param userRouter The Angular router that the loader will listen to navigation events on.
	 * @returns The loader's subscription to router events, which can be for removing the subscription as needed.
	 */
    forceOnNavigate (userRouter: Router): Subscription {
        return userRouter.events.subscribe((event: Event) => {
            switch (true) {
                case event instanceof NavigationStart: {
                    this.force(true);
                    break;
                }
                case event instanceof NavigationEnd:
                case event instanceof NavigationCancel:
                case event instanceof NavigationError: {
                    this.force(false);
                    break;
                }
                default: {
                    break;
                }
            }
        });
    }

    /**
	 * Return the current activity status of a channel as an observable where `true` means there is activity on the channel.
	 *
	 * @param channelName The name of the channel to retrieve an observable for.
	 */
    getLoadingStatus = (channelName: ChannelName) => this._globalChannels[channelName].getStatus;

    /** @ignore A convenience method for toggling the HTTP status on and off. This is leveraged by the `LoadingInterceptor`. */
    setHttpStatus = (status = false) => {
        this._globalChannels.http.setStatus = status;
    };

    // /** @ignore Binds the client options to the global Loader so any following events use them. */
    // initService = () => { };

    // constructor () { this.initService(); }
}
